import { CellContext, ColumnDef } from "@tanstack/react-table";
import { format, parseISO } from "date-fns";

import {
  renderRiskByStatus,
  renderRiskIconsAxis,
} from "src/brand/EC/components/folders-list/riskCell.helper";
import EntrepriseIcon from "src/core/assets/images/icons/entreprise.svg?react";
import HumanIcon from "src/core/assets/images/icons/human.svg?react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "src/core/components/ui/tooltip";
import { Status } from "src/core/constants/folders";
import { useFolderListContext } from "src/core/contexts/FolderListContext";
import { FolderListOf, FolderListStrategy } from "src/core/types/folderListStrategy";

const FollowersCell = ({
  followers,
}: {
  followers: { id: string; name: string }[] | undefined;
}) => {
  const { followedByUserIdFilter } = useFolderListContext();

  if (!followers || followers.length === 0) {
    return "-";
  }

  // Si un filtre est actif, trouver l'utilisateur correspondant
  let selectedFollower: { id: string; name: string } | undefined;
  let otherFollowers: { id: string; name: string }[] = [];

  if (followedByUserIdFilter && followedByUserIdFilter.trim() !== "") {
    const selectedIndex = followers.findIndex((f) => f.id === followedByUserIdFilter);
    if (selectedIndex !== -1) {
      selectedFollower = followers[selectedIndex];
      otherFollowers = followers.filter((_, index) => index !== selectedIndex);
    } else {
      // Si l'utilisateur sélectionné n'est pas dans la liste, comportement par défaut
      selectedFollower = followers[0];
      otherFollowers = followers.slice(1);
    }
  } else {
    // Comportement par défaut : premier dans la colonne, les autres dans l'infobulle
    selectedFollower = followers[0];
    otherFollowers = followers.slice(1);
  }

  if (otherFollowers.length === 0) {
    return selectedFollower.name;
  }

  return (
    <div className="flex items-center gap-2">
      <span>{selectedFollower.name}</span>
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <div className="chip-default">+{otherFollowers.length}</div>
          </TooltipTrigger>
          <TooltipContent side="bottom" align="center">
            <div className="flex flex-col gap-1">
              {otherFollowers.map((follower) => (
                <div key={follower.id}>{follower.name}</div>
              ))}
            </div>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    </div>
  );
};

export const FolderListStrategyEC: FolderListStrategy<"EC"> = {
  buildColumns: ({ status, t }): ColumnDef<FolderListOf<"EC">>[] => {
    const columns: ColumnDef<FolderListOf<"EC">>[] = [
      {
        accessorKey: "raisonSociale",
        header: t("folders.list.columns.raisonSociale"),
        enableSorting: false,
        accessorFn: (row: FolderListOf<"EC">) => row.identity ?? "",
        cell: ({ getValue, row }: CellContext<FolderListOf<"EC">, unknown>) => {
          const value = (getValue() as string | undefined) || "-";
          const Icon = row.original.isPPH ? HumanIcon : EntrepriseIcon;
          return (
            <div className="flex flex-row items-center gap-x-5">
              <div className="w-20 h-20 flex items-center justify-center">
                <Icon className="h-[15px]" />
              </div>
              <span>{value}</span>
            </div>
          );
        },
      },
      {
        accessorKey: "siren",
        header: t("folders.list.columns.siren"),
        enableSorting: false,
        accessorFn: (row: FolderListOf<"EC">) => row.registration ?? "",
        cell: ({ getValue }: CellContext<FolderListOf<"EC">, unknown>) =>
          (getValue() as string | undefined) || "-",
      },
      {
        accessorKey: "expertComptable",
        header: t("folders.list.columns.expertComptable"),
        enableSorting: false,
        accessorFn: (row: FolderListOf<"EC">) => row.inCharge ?? "",
        cell: ({ getValue }: CellContext<FolderListOf<"EC">, unknown>) =>
          (getValue() as string | undefined) || "-",
      },
      {
        accessorKey: "risk",
        header:
          status === Status.VALIDATION_PENDING_HR
            ? t("folders.list.columns.riskHR")
            : t("folders.list.columns.risk"),
        enableSorting: status === Status.VALIDATION_PENDING_HR ? false : true,
        cell: ({ row }: CellContext<FolderListOf<"EC">, unknown>) =>
          renderRiskByStatus(status, row.original?.riskDisplay, t),
      },
    ];
    if (status !== Status.DRAFT) {
      columns.push({
        accessorKey: "axes",
        header: t("folders.list.columns.axes"),
        enableSorting: false,
        accessorFn: (row: FolderListOf<"EC">) => row.updatedAt ?? "",
        cell: ({ row }: CellContext<FolderListOf<"EC">, unknown>) =>
          renderRiskIconsAxis(row.original?.riskIcons),
      });
    }
    columns.push(
      {
        accessorKey: "updatedAt",
        header: t(
          `folders.list.columns.${
            status === Status.RENEWAL
              ? // @TODO: dès que mis en place côté backend, on remet "renewalDate"
                "updatedAt" //"renewalDate"
              : status === Status.DRAFT
              ? "updatedAt"
              : status === Status.VALIDATED
              ? // @TODO: dès que mis en place côté backend, on remet "revalidationDate"
                "updatedAt" //"revalidationDate"
              : "completionDate"
          }`
        ),
        enableSorting: true,
        accessorFn: (row: FolderListOf<"EC">) => row.updatedAt ?? "",
        cell: ({ getValue }: CellContext<FolderListOf<"EC">, unknown>) => {
          const v = getValue() as string | undefined;
          return v ? format(parseISO(v), "dd/MM/yyyy") : "";
        },
      },
      {
        accessorKey: "followers",
        header: t("folders.list.columns.followers"),
        enableSorting: false,
        accessorFn: (row: FolderListOf<"EC">) => row.followers ?? "",
        cell: ({ getValue }: CellContext<FolderListOf<"EC">, unknown>) => {
          return (
            <FollowersCell followers={getValue() as { id: string; name: string }[] | undefined} />
          );
        },
      }
    );
    return columns;
  },
};
